import React, { useState } from 'react';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Separator } from './ui/separator';
import { X, Download, FileText, FileSpreadsheet, Image, Loader2, CheckCircle2, Package } from 'lucide-react';
import { ExportOptions, ExportData, exportResults } from '../utils/exportUtils';
import { toast } from 'sonner';

interface ExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  exportData: ExportData;
}

const ExportModal: React.FC<ExportModalProps> = ({ isOpen, onClose, exportData }) => {
  const [options, setOptions] = useState<ExportOptions>({
    pdf: true,
    pdfIncludeIndividualImages: false,
    pdfIncludePerImageHistograms: false,
    excel: false,
    csv: false,
    detectionImages: false,
  });

  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState<string>('');

  if (!isOpen) return null;

  const selectedCount = [options.pdf, options.excel, options.csv, options.detectionImages].filter(Boolean).length;
  const willZip = selectedCount > 1 || options.csv;

  const handleExport = async () => {
    if (selectedCount === 0) {
      toast.error('Please select at least one export format');
      return;
    }

    setIsExporting(true);
    setExportProgress('Preparing export...');

    try {
      await exportResults(exportData, options, (message) => {
        setExportProgress(message);
      });
      toast.success('Export completed successfully!');
      onClose();
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Export failed', {
        description: error instanceof Error ? error.message : 'Unknown error'
      });
    } finally {
      setIsExporting(false);
      setExportProgress('');
    }
  };

  const toggleOption = (key: keyof ExportOptions) => {
    setOptions(prev => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b dark:border-gray-800">
          <div className="flex items-center gap-2">
            <Download className="w-5 h-5 text-primary" />
            <h2 className="text-lg font-semibold text-foreground">Export Results</h2>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} disabled={isExporting}>
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {/* Summary */}
          <div className="bg-muted/30 dark:bg-gray-800/50 rounded-lg p-3 text-sm">
            <p className="text-muted-foreground">
              Exporting <span className="font-semibold text-foreground">{exportData.batchAnalysis.totalDetections} detections</span> from{' '}
              <span className="font-semibold text-foreground">{exportData.batchAnalysis.totalImages} images</span>
            </p>
          </div>

          <Separator />

          {/* Format Options */}
          <div className="space-y-3">
            <Label className="text-sm font-semibold">Select Export Formats</Label>

            {/* PDF Option */}
            <div className="space-y-2">
              <label className="flex items-center gap-3 p-3 rounded-lg border dark:border-gray-700 cursor-pointer hover:bg-muted/30 dark:hover:bg-gray-800/50 transition-colors">
                <input
                  type="checkbox"
                  checked={options.pdf}
                  onChange={() => toggleOption('pdf')}
                  className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                />
                <FileText className="w-5 h-5 text-red-500" />
                <div className="flex-1">
                  <p className="font-medium text-sm text-foreground">PDF Report</p>
                  <p className="text-xs text-muted-foreground">Batch summary, statistics, and histograms</p>
                </div>
              </label>

              {options.pdf && (
                <div className="ml-10 space-y-2">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={options.pdfIncludeIndividualImages}
                      onChange={() => toggleOption('pdfIncludeIndividualImages')}
                      className="w-3.5 h-3.5 rounded border-gray-300 text-primary focus:ring-primary"
                    />
                    <span className="text-muted-foreground">Include individual annotated images</span>
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={options.pdfIncludePerImageHistograms}
                      onChange={() => toggleOption('pdfIncludePerImageHistograms')}
                      className="w-3.5 h-3.5 rounded border-gray-300 text-primary focus:ring-primary"
                    />
                    <span className="text-muted-foreground">Include per-image histograms</span>
                  </label>
                </div>
              )}
            </div>

            {/* Excel Option */}
            <label className="flex items-center gap-3 p-3 rounded-lg border dark:border-gray-700 cursor-pointer hover:bg-muted/30 dark:hover:bg-gray-800/50 transition-colors">
              <input
                type="checkbox"
                checked={options.excel}
                onChange={() => toggleOption('excel')}
                className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
              />
              <FileSpreadsheet className="w-5 h-5 text-green-600" />
              <div className="flex-1">
                <p className="font-medium text-sm text-foreground">Excel (.xlsx)</p>
                <p className="text-xs text-muted-foreground">3 sheets: Batch Summary, Per-Image, All Detections</p>
              </div>
            </label>

            {/* CSV Option */}
            <label className="flex items-center gap-3 p-3 rounded-lg border dark:border-gray-700 cursor-pointer hover:bg-muted/30 dark:hover:bg-gray-800/50 transition-colors">
              <input
                type="checkbox"
                checked={options.csv}
                onChange={() => toggleOption('csv')}
                className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
              />
              <FileText className="w-5 h-5 text-blue-500" />
              <div className="flex-1">
                <p className="font-medium text-sm text-foreground">CSV Files</p>
                <p className="text-xs text-muted-foreground">3 files: batch_summary, per_image, all_detections</p>
              </div>
            </label>

            {/* Detection Images Option */}
            <label className="flex items-center gap-3 p-3 rounded-lg border dark:border-gray-700 cursor-pointer hover:bg-muted/30 dark:hover:bg-gray-800/50 transition-colors">
              <input
                type="checkbox"
                checked={options.detectionImages}
                onChange={() => toggleOption('detectionImages')}
                className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
              />
              <Image className="w-5 h-5 text-purple-500" />
              <div className="flex-1">
                <p className="font-medium text-sm text-foreground">Detection Images</p>
                <p className="text-xs text-muted-foreground">Annotated images with bounding boxes</p>
              </div>
            </label>
          </div>

          <Separator />

          {/* Output Info */}
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            {willZip ? (
              <>
                <Package className="w-4 h-4" />
                <span>Output: ZIP archive with selected files</span>
              </>
            ) : selectedCount === 1 ? (
              <>
                <CheckCircle2 className="w-4 h-4 text-green-500" />
                <span>Output: Direct file download</span>
              </>
            ) : (
              <span>Select at least one format</span>
            )}
          </div>

          {/* Export Progress */}
          {isExporting && exportProgress && (
            <div className="flex items-center gap-2 text-sm text-primary bg-primary/5 rounded-lg p-3">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>{exportProgress}</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 p-4 border-t dark:border-gray-800 bg-muted/20 dark:bg-gray-800/30">
          <Button variant="outline" onClick={onClose} disabled={isExporting}>
            Cancel
          </Button>
          <Button onClick={handleExport} disabled={isExporting || selectedCount === 0}>
            {isExporting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Exporting...
              </>
            ) : (
              <>
                <Download className="w-4 h-4 mr-2" />
                Export {selectedCount > 0 ? `(${selectedCount} format${selectedCount > 1 ? 's' : ''})` : ''}
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ExportModal;
